import { Checkbox, Divider, Skeleton, Stack } from '@mui/material';
import { ChangeEvent, isValidElement, ReactElement, ReactNode, useEffect, useState } from 'react';
import { Typography } from '../typography';
import {
  ItemWrapper,
  ListActionContainer,
  ListActionWrapper,
  ListContainer,
  ListFooter,
  ListHeader,
  ListWrapper,
} from './List.styles';
import _ from 'lodash';
export type ListDataKey<T> = keyof T;

interface Field<T> {
  title: ReactNode | string; //header display title
  key?: ListDataKey<T>; //object key
  path?: string[]; //if nested field
  valueGetter?: (item: T) => string | number;
  cellRenderer?: (item: T) => ReactNode;
}

interface ListProps<T> {
  list: T[];
  fields: Field<T>[];
  rowRenderer?: ({ ...rest }: RowRendererProps<T>) => ReactNode;
  count: number;
  loading?: boolean;
  keyGetter: (item: T, idx: number) => string;
  onItemClick?: (item: T) => void;
  actions?: ReactNode;
  gridTemplateColumns?: string;
  selectedIds?: string[];
  onSelectedIdsChange?: (newIds: string[]) => void;
  keepSelectedIds?: boolean;
  onlyOne?: boolean;
  getSelectedIdsCountLabel?: (selectedIds: number) => string;
}

interface RowRendererProps<T> {
  item: T;
  key: string;
  selected: boolean;
  handleCheckboxChange: (e: ChangeEvent, checked: boolean) => void;
  handleClick: () => void;
  gridTemplateColumns: string;
  isSelection: boolean;
}

export const FlatList = <T extends unknown>({
  list,
  keyGetter,
  fields,
  rowRenderer,
  count,
  loading,
  onItemClick,
  actions,
  gridTemplateColumns,
  selectedIds,
  onSelectedIdsChange,
  onlyOne,
  keepSelectedIds,
  getSelectedIdsCountLabel,
}: ListProps<T>): ReactElement => {
  const [checkAll, setCheckAll] = useState(false);

  const handleCheckAllChange = (_event: ChangeEvent, checked: boolean) => {
    if (onSelectedIdsChange) {
      if (checked) {
        let ids = list.map((el, idx) => keyGetter(el, idx));
        if (keepSelectedIds) {
          let intersection = _.union(selectedIds, ids);
          onSelectedIdsChange(intersection);
        } else {
          onSelectedIdsChange(ids);
        }
      } else {
        if (keepSelectedIds) {
          let ids = list.map((el, idx) => keyGetter(el, idx));
          if (selectedIds) {
            let selectedIdsTemp = [...selectedIds];
            _.remove(selectedIdsTemp, (el) => ids.includes(el));
            onSelectedIdsChange(selectedIdsTemp);
          }
        } else {
          onSelectedIdsChange([]);
        }
      }
    }
  };

  useEffect(() => {
    if (loading) {
      setCheckAll(false);
    } else {
      let ids = list.map((el, idx) => keyGetter(el, idx));
      let allChecked = true;
      for (let index = 0; index < ids.length; index++) {
        if (!selectedIds?.includes(ids[index])) {
          allChecked = false;
          break;
        }
      }
      setCheckAll(allChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, list, loading]);

  const getGridTemplateColumns = () => {
    let ret = '';
    if (gridTemplateColumns) {
      ret = gridTemplateColumns;
    } else {
      let columns = '';
      fields.forEach(() => {
        columns = columns + '1fr ';
      });
      ret = columns;
    }
    if (selectedIds) {
      ret = '0.5fr ' + ret;
    }
    return ret;
  };

  const renderLoading = (): ReactNode[] => {
    let els: ReactNode[] = [];
    for (let i = 0; i < count; i++) {
      els.push(
        <ItemWrapper gridTemplateColumns={getGridTemplateColumns()} key={i}>
          {selectedIds && (
            <div>
              <Checkbox />
            </div>
          )}
          {fields.map(() => (
            <Skeleton width={'80%'} />
          ))}
        </ItemWrapper>,
      );
    }
    return els;
  };

  const handleItemClick = (item: T) => {
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const renderItem = (item: T, idx: number) => {
    const handleCheckboxChange = (e: ChangeEvent, checked: boolean) => {
      e.stopPropagation();
      let temp = selectedIds ? [...selectedIds] : [];
      if (onlyOne) {
        if (checked) {
          onSelectedIdsChange && onSelectedIdsChange([keyGetter(item, idx)]);
        } else {
          onSelectedIdsChange && onSelectedIdsChange([]);
        }
      } else {
        if (checked) {
          temp.push(keyGetter(item, idx));
          onSelectedIdsChange && onSelectedIdsChange(temp);
        } else {
          onSelectedIdsChange && onSelectedIdsChange(temp.filter((e) => e !== keyGetter(item, idx)));
        }
      }
    };

    const handleClick = () => {
      handleItemClick(item);
    };

    const renderCell = (field: Field<T>) => {
      if (field.cellRenderer) {
        return field.cellRenderer(item);
      } else if (field.valueGetter) {
        return <Typography truncate>{field.valueGetter(item)}</Typography>;
      } else if (field.key) {
        return (
          <Typography truncate>
            {field.path ? _.get(item, [field.key, ...field.path]) : _.get(item, field.key)}
          </Typography>
        );
      } else {
        return 'ND';
      }
    };

    if (rowRenderer) {
      return rowRenderer({
        item: item,
        key: keyGetter(item, idx),
        gridTemplateColumns: getGridTemplateColumns(),
        handleCheckboxChange: handleCheckboxChange,
        handleClick: handleClick,
        isSelection: selectedIds !== undefined,
        selected: selectedIds !== undefined && selectedIds?.includes(keyGetter(item, idx)),
      });
    } else {
      return (
        <ItemWrapper
          selected={selectedIds?.includes(keyGetter(item, idx))}
          gridTemplateColumns={getGridTemplateColumns()}
          key={keyGetter(item, idx)}
          onClick={handleClick}
        >
          {selectedIds && (
            <div>
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                checked={selectedIds?.includes(keyGetter(item, idx))}
                onChange={handleCheckboxChange}
              />
            </div>
          )}
          {fields.map((el) => renderCell(el))}
        </ItemWrapper>
      );
    }
  };

  return (
    <>
      {actions && (
        <ListActionWrapper>
          <ListActionContainer>
            <Stack
              direction="row"
              alignItems={'center'}
              spacing={3}
              sx={{ paddingX: '10px' }}
              divider={<Divider orientation="vertical" flexItem />}
            >
              {actions}
            </Stack>
          </ListActionContainer>
        </ListActionWrapper>
      )}
      <ListWrapper corner={isValidElement(actions)}>
        <ListHeader gridTemplateColumns={getGridTemplateColumns()}>
          {selectedIds && <div>{!onlyOne && <Checkbox checked={checkAll} onChange={handleCheckAllChange} />}</div>}
          {fields.map((el) => {
            if (isValidElement(el)) {
              return el;
            } else {
              return (
                <Typography fontWeight={600} fontSize={'20px'}>
                  {el.title}
                </Typography>
              );
            }
          })}
        </ListHeader>
        <ListContainer>{loading ? renderLoading() : list.map((el, idx) => renderItem(el, idx))}</ListContainer>
        <ListFooter>
          <div>
            {selectedIds && getSelectedIdsCountLabel && (
              <Typography fontSize={'12px'}>{getSelectedIdsCountLabel(selectedIds.length)}</Typography>
            )}
          </div>
        </ListFooter>
      </ListWrapper>
    </>
  );
};
